import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './components/layout/AppShell';
import { PublicShell } from './components/layout/PublicShell';
import { ProtectedRoute, AdminRoute, SellerRoute } from './components/auth/Guards';

// Public
import LandingPage from './pages/Landing';
import SignInPage from './pages/auth/SignIn';
import SignUpPage from './pages/auth/SignUp';
import VerifyEmailPage from './pages/auth/VerifyEmail';
import ForgotPasswordPage from './pages/auth/ForgotPassword';
import TermsPage from './pages/legal/Terms';
import PrivacyPage from './pages/legal/Privacy';
import CookiePolicyPage from './pages/legal/CookiePolicy';
import RefundsPage from './pages/legal/Refunds';
import SellerAgreementPage from './pages/legal/SellerAgreement';
import CommunityGuidelinesPage from './pages/legal/CommunityGuidelines';

// Authenticated
import DiscoverPage from './pages/Discover';
import ServiceDetailPage from './pages/ServiceDetail';
import SellerProfilePage from './pages/SellerProfile';
import BookingsPage from './pages/Bookings';
import BookingDetailPage from './pages/BookingDetail';
import ProfilePage from './pages/Profile';
import CampusControlPage from './pages/CampusControl';
import VerifyStudentPage from './pages/VerifyStudent';
import NotificationsPage from './pages/Notifications';
import FavouritesPage from './pages/Favourites';
import RequestsPage from './pages/requests/Requests';
import RequestNewPage from './pages/requests/RequestNew';
import RequestDetailPage from './pages/requests/RequestDetail';
import SellerRequestsPage from './pages/seller/Requests';
import SellerRequestDetailPage from './pages/seller/RequestDetail';
import SettingsAccountPage from './pages/settings/Account';
import SettingsPaymentPage from './pages/settings/Payment';
import SettingsNotificationsPage from './pages/settings/Notifications';

// Seller + admin clusters are code-split (React.lazy): a buyer never loads the
// seller Studio or the admin console, keeping the initial bundle small on mobile.
const SellerOnboardingPage = lazy(() => import('./pages/seller/Onboarding'));
const SellerDashboardPage = lazy(() => import('./pages/seller/Dashboard'));
const SellerServicesPage = lazy(() => import('./pages/seller/Services'));
const SellerServiceNewPage = lazy(() => import('./pages/seller/ServiceNew'));
const SellerServiceEditPage = lazy(() => import('./pages/seller/ServiceEdit'));
const SellerAvailabilityPage = lazy(() => import('./pages/seller/Availability'));
const SellerReviewsPage = lazy(() => import('./pages/seller/Reviews'));
const SellerEarningsPage = lazy(() => import('./pages/seller/Earnings'));

const AdminDashboardPage = lazy(() => import('./pages/admin/Dashboard'));
const AdminUsersPage = lazy(() => import('./pages/admin/Users'));
const AdminUserDetailPage = lazy(() => import('./pages/admin/UserDetail'));
const AdminServicesPage = lazy(() => import('./pages/admin/Services'));
const AdminBookingsPage = lazy(() => import('./pages/admin/Bookings'));
const AdminTransactionsPage = lazy(() => import('./pages/admin/Transactions'));
const AdminReportsPage = lazy(() => import('./pages/admin/Reports'));
const AdminVerificationsPage = lazy(() => import('./pages/admin/Verifications'));
const AdminPromoCodesPage = lazy(() => import('./pages/admin/PromoCodes'));
const AdminCategoriesPage = lazy(() => import('./pages/admin/Categories'));
const AdminAnnouncementsPage = lazy(() => import('./pages/admin/Announcements'));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/Analytics'));
const AdminSellersPage = lazy(() => import('./pages/admin/Sellers'));
const AdminEconomicsPage = lazy(() => import('./pages/admin/Economics'));

import NotFoundPage from './pages/NotFound';

// Feed is kept alive in AppShell — this route only marks /feed as active for nav.
function FeedRoute() {
  return null;
}

export default function App() {
  return (
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center text-makhe-muted">Loading…</div>
      }
    >
      <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth/sign-in" element={<SignInPage />} />
      <Route path="/auth/sign-up" element={<SignUpPage />} />
      <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/legal/terms" element={<TermsPage />} />
      <Route path="/legal/privacy" element={<PrivacyPage />} />
      <Route path="/legal/refunds" element={<RefundsPage />} />
      <Route path="/legal/seller-agreement" element={<SellerAgreementPage />} />
      <Route path="/legal/community-guidelines" element={<CommunityGuidelinesPage />} />
      <Route path="/legal/cookie-policy" element={<CookiePolicyPage />} />

      {/* Public browsing — viewable without signing in so shared service links
          open. Signed-in users get the full app chrome; guests get a light
          header. Transacting (book/save/request) still requires auth. */}
      <Route element={<PublicShell />}>
        <Route path="/discover" element={<DiscoverPage />} />
        <Route path="/service/:serviceId" element={<ServiceDetailPage />} />
      </Route>

      {/* Authenticated */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/feed" element={<FeedRoute />} />
        <Route path="/seller/:sellerId" element={<SellerProfilePage />} />
        <Route path="/bookings" element={<BookingsPage />} />
        <Route path="/bookings/:bookingId" element={<BookingDetailPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/campus-control" element={<CampusControlPage />} />
        <Route path="/profile/verify-student" element={<VerifyStudentPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/favourites" element={<FavouritesPage />} />
        <Route path="/requests" element={<RequestsPage />} />
        <Route path="/requests/new" element={<RequestNewPage />} />
        <Route path="/requests/:requestId" element={<RequestDetailPage />} />
        <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
        <Route path="/settings/account" element={<SettingsAccountPage />} />
        <Route path="/settings/payment" element={<SettingsPaymentPage />} />
        <Route path="/settings/notifications" element={<SettingsNotificationsPage />} />

        {/* Seller-only */}
        <Route
          path="/seller/onboarding"
          element={
            <SellerRoute requireSeller={false}>
              <SellerOnboardingPage />
            </SellerRoute>
          }
        />
        <Route
          path="/seller/dashboard"
          element={
            <SellerRoute>
              <SellerDashboardPage />
            </SellerRoute>
          }
        />
        <Route
          path="/seller/services"
          element={
            <SellerRoute>
              <SellerServicesPage />
            </SellerRoute>
          }
        />
        <Route
          path="/seller/services/new"
          element={
            <SellerRoute>
              <SellerServiceNewPage />
            </SellerRoute>
          }
        />
        <Route
          path="/seller/services/:id/edit"
          element={
            <SellerRoute>
              <SellerServiceEditPage />
            </SellerRoute>
          }
        />
        <Route
          path="/seller/availability"
          element={
            <SellerRoute>
              <SellerAvailabilityPage />
            </SellerRoute>
          }
        />
        <Route
          path="/seller/reviews"
          element={
            <SellerRoute>
              <SellerReviewsPage />
            </SellerRoute>
          }
        />
        <Route
          path="/seller/earnings"
          element={
            <SellerRoute>
              <SellerEarningsPage />
            </SellerRoute>
          }
        />
        <Route
          path="/seller/requests"
          element={
            <SellerRoute>
              <SellerRequestsPage />
            </SellerRoute>
          }
        />
        <Route
          path="/seller/requests/:requestId"
          element={
            <SellerRoute>
              <SellerRequestDetailPage />
            </SellerRoute>
          }
        />

        {/* Admin-only */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <AdminUsersPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users/:userId"
          element={
            <AdminRoute>
              <AdminUserDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/services"
          element={
            <AdminRoute>
              <AdminServicesPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/bookings"
          element={
            <AdminRoute>
              <AdminBookingsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/transactions"
          element={
            <AdminRoute>
              <AdminTransactionsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <AdminRoute>
              <AdminReportsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/verifications"
          element={
            <AdminRoute>
              <AdminVerificationsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/promo-codes"
          element={
            <AdminRoute>
              <AdminPromoCodesPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/categories"
          element={
            <AdminRoute>
              <AdminCategoriesPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/announcements"
          element={
            <AdminRoute>
              <AdminAnnouncementsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/sellers"
          element={
            <AdminRoute>
              <AdminSellersPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/economics"
          element={
            <AdminRoute>
              <AdminEconomicsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <AdminRoute>
              <AdminAnalyticsPage />
            </AdminRoute>
          }
        />
      </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
